/**
 * The SUNSET tag (Almanac S3, Decision 6; D4, D5).
 *
 * A stop is tagged when it starts between 60 minutes before and 10 minutes
 * after a sunset, inclusive at both ends, compared as instants. Two sunsets are
 * read, both through S2's `sunTimes`: the solar cycle nearest the stop start
 * (`sunTimes(stopStart)`) and the cycle before it (`sunTimes(stopStart − 24 h)`).
 * One read alone misses a stop whose sunset falls more than about 11 h 50 min
 * after its own noon, which happens at high latitudes — the last midnight-sun
 * evening in Tromsø is one. A polar read (`null`) contributes nothing.
 *
 * The stop start is the item's date and time in the plan's serialized
 * `effective_timezone`, turned into an instant by `zonedInstant` — the one zone
 * the server localized the plan in. Nothing here reads the device's zone or
 * the sun library directly: the zone reaches the rule only through the reader,
 * and the sun only through `sunTimes`. `outdoor` is not read (D5).
 * Mirrored at `mobile/src/lib/sunsetTag.ts`; both are bound by
 * `docs/plans/almanac-tag-vectors.json`.
 */
import { sunTimes } from './sun';
import { zonedInstant } from './zonedInstant';

const MINUTE_MS = 60 * 1000;
/** The window opens this long before a sunset. */
export const SUNSET_WINDOW_BEFORE_MS = 60 * MINUTE_MS;
/** And closes this long after it. */
export const SUNSET_WINDOW_AFTER_MS = 10 * MINUTE_MS;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** Whether one sunset's window, [sunset − 60 min, sunset + 10 min], holds the stop. */
export function windowHolds(stopStart: Date, sunset: Date | null): boolean {
  if (!sunset) return false;
  const delta = stopStart.getTime() - sunset.getTime();
  if (Number.isNaN(delta)) return false;
  return delta >= -SUNSET_WINDOW_BEFORE_MS && delta <= SUNSET_WINDOW_AFTER_MS;
}

/**
 * The comparator: tagged iff any candidate's window holds the stop. `null`
 * candidates (a polar cycle) contribute nothing, so two `null`s are no tag.
 */
export function tagFromSunsets(stopStart: Date, sunsets: ReadonlyArray<Date | null>): boolean {
  return sunsets.some((s) => windowHolds(stopStart, s));
}

/** The two sunsets the rule reads: the nearest cycle's, then the previous one's. */
export function sunsetCandidates(stopStart: Date, lat: number, lng: number): [Date | null, Date | null] {
  const nearest = sunTimes(stopStart, lat, lng);
  const previous = sunTimes(new Date(stopStart.getTime() - DAY_MS), lat, lng);
  return [nearest ? nearest.sunset : null, previous ? previous.sunset : null];
}

/** Whether a stop starting at `stopStart` at (lat, lng) is a sunset stop. */
export function isSunsetStop(stopStart: Date, lat: number, lng: number): boolean {
  return tagFromSunsets(stopStart, sunsetCandidates(stopStart, lat, lng));
}

export interface TagItem {
  date: string | null | undefined;
  time: string | null | undefined;
  latitude?: number | null;
  longitude?: number | null;
}

export interface TagPlan {
  /** The plan's serialized `effective_timezone`; absent means no tag. */
  effective_timezone?: string | null;
  destination_lat?: number | null;
  destination_lng?: number | null;
}

function point(lat: number | null | undefined, lng: number | null | undefined): { lat: number; lng: number } | null {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
    ? { lat, lng }
    : null;
}

/** The stop's own coordinates, else the plan's destination, else `null`. */
export function stopCoordinates(item: TagItem, plan: TagPlan): { lat: number; lng: number } | null {
  return point(item.latitude, item.longitude) ?? point(plan.destination_lat, plan.destination_lng);
}

/** The stop-start instant, or `null` without a date, a time or a readable zone. */
export function stopStart(item: TagItem, plan: TagPlan): Date | null {
  if (!item.date || !item.time || !plan.effective_timezone) return null;
  return zonedInstant(item.date, item.time, plan.effective_timezone);
}

/**
 * Whether an itinerary item gets the SUNSET tag. No time, no coordinates, no
 * transported zone or an unreadable one: no tag.
 */
export function sunsetTagFor(item: TagItem, plan: TagPlan): boolean {
  const at = stopCoordinates(item, plan);
  if (!at) return false;
  const start = stopStart(item, plan);
  if (!start) return false;
  return isSunsetStop(start, at.lat, at.lng);
}
